// Beperkt de vragenlijst tot de vragen die op één meetmoment gesteld worden.
//
// Een meetmoment draagt de UITSLUITINGEN (`MeasureMoments.ExcludedFieldIDs`),
// niet de insluitingen: ontbreekt het veld, dan staat alles aan. Zo hoeven
// bestaande meetmomenten niet gemigreerd te worden en staat een later
// toegevoegde vraag automatisch overal aan.
//
// Draait op elk invulpad — publieke vragenlijst, AI-interviewer, print — zodat
// er geen kanaal is waar een uitgezette vraag alsnog langskomt. Eén helper,
// drie afnemers.
// AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook

import isStandardQuestion from './isStandardQuestion'

/**
 * @param {Array<object>} fields - QuestionnaireFields van één vragenlijst,
 *   inclusief de sectiemarkeringen (`Type: 'section'`), in weergavevolgorde.
 * @param {Array<string>} [excludedFieldIDs] - `QuestionnaireFields.ID`'s die op
 *   dit meetmoment niet gesteld worden. Leeg/afwezig = alle vragen aan.
 * @returns {Array<object>} Dezelfde velden in dezelfde volgorde, zonder de
 *   uitgesloten vragen en zonder sectiekoppen die daardoor leeg achterblijven.
 */
const filterFieldsForMoment = (fields, excludedFieldIDs) => {
    if (!Array.isArray(fields)) return []

    const excluded = new Set(excludedFieldIDs || [])
    if (excluded.size === 0) return fields

    // Een sectie hoort niet in de uitsluitingslijst te staan (de modal schakelt
    // de kinderen), maar als het toch gebeurt mogen die kinderen niet als losse
    // vragen achterblijven.
    // Meetstandaardvragen blijven altijd staan, ook als een oude uitsluitingslijst
    // ze noemt: een MSI-effect heeft elk meetmoment nodig om te kunnen vergelijken.
    const isExcluded = (field) => (
        !isStandardQuestion(field) && (excluded.has(field?.ID) || excluded.has(field?.SectionID))
    )

    const remaining = fields.filter((field) => !isExcluded(field))

    const sectionsWithQuestions = new Set(
        remaining
            .filter((field) => field?.Type !== 'section' && field?.SectionID)
            .map((field) => field.SectionID)
    )

    // Een lege sectiekop is voor de respondent een kop zonder vragen eronder.
    return remaining.filter((field) => (
        field?.Type !== 'section' || sectionsWithQuestions.has(field?.ID)
    ))
}

export default filterFieldsForMoment
